// External libraries
import { useCallback, useEffect, useRef, useState } from "react";

// FontAwesome icons
import {
  faAngleLeft,
  faAngleRight,
  faFolder,
  faGear,
  faLock,
  faLockOpen,
  faWindowRestore,
} from "@fortawesome/pro-duotone-svg-icons";
import ColorChangingIcon from "../../Common/UI/ColorChangingIcon";

// Kendo React components
import {
  moveTreeViewItem,
  TreeView,
  TreeViewDragAnalyzer,
  TreeViewDragClue,
} from "@progress/kendo-react-treeview";

// Local components
import {
  CreateEntryTemplateButton,
  EditDatasetButton,
  EditTemplateButton,
  ViewDatasetButton,
  ViewEntryTemplateButton,
  ViewTemplateButton,
} from "../../Buttons/HomepageButtons";

// Local signals
import { tileTreeViewConfigs, treeViewDragEnabled } from "./HomepageSignals";

// CustomTreeView Component
export const CustomTreeView = ({
  tileId,
  treeViewConfig,
  onConfigClick,
  iconConfig,
  treeData,
  isLocked,
}) => {
  const containerRef = useRef(null);
  const dragClueRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [expanded, setExpanded] = useState(treeViewConfig.expanded ?? true);
  const [expandedItems, setExpandedItems] = useState({});
  const isDragEnabled = treeViewDragEnabled.value[treeViewConfig.id] || false;

  // Reset drag mode when lock is disabled
  useEffect(() => {
    if (!isLocked && isDragEnabled) {
      treeViewDragEnabled.value = {
        ...treeViewDragEnabled.value,
        [treeViewConfig.id]: false,
      };
    }
  }, [isDragEnabled, treeViewConfig.id, isLocked]);

  // Toggle drag mode
  const toggleDragMode = useCallback(() => {
    if (isLocked) {
      const newState = !isDragEnabled;

      // Update signal
      treeViewDragEnabled.value = {
        ...treeViewDragEnabled.value,
        [treeViewConfig.id]: newState,
      };

      // Collapse all items when enabling drag
      if (newState) {
        setExpandedItems({});
      }
    }
  }, [isDragEnabled, treeViewConfig.id, isLocked]);

  // Toggle item expansion (show/hide action buttons)
  const toggleItemExpanded = useCallback((itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  }, []);

  // Drag helpers
  const getSiblings = useCallback((index, data) => {
    let result = data;
    const parts = index.split("_").map(Number);
    for (let i = 0; i < parts.length - 1; i++) {
      result = result[parts[i]].items || [];
    }
    return result;
  }, []);

  const getClueClassName = useCallback(
    (event) => {
      const analyzer = new TreeViewDragAnalyzer(event).init();
      const index = analyzer.destinationMeta.itemHierarchicalIndex;

      if (index === "0") return "k-i-cancel";
      if (!analyzer.isDropAllowed) return "k-i-cancel";

      switch (analyzer.getDropOperation()) {
        case "before":
          return index.endsWith("_0") ? "k-i-insert-up" : "k-i-insert-middle";
        case "after": {
          const siblings = getSiblings(index, treeData);
          const last = Number(index.split("_").pop());
          return last < siblings.length - 1 ? "k-i-insert-middle" : "k-i-insert-down";
        }
        default:
          return "k-i-cancel";
      }
    },
    [treeData, getSiblings]
  );

  const onItemDragOver = useCallback(
    (event) => {
      if (dragClueRef.current) {
        const containerRect = containerRef.current?.getBoundingClientRect();
        const offsetY = containerRect ? containerRect.top : 0;
        const offsetX = containerRect ? containerRect.left : 0;

        dragClueRef.current.show(
          event.pageY - offsetY - 20,
          event.pageX - offsetX + 20,
          event.item.text,
          getClueClassName(event)
        );
      }
    },
    [getClueClassName]
  );

  const onItemDragEnd = useCallback(
    (event) => {
      if (dragClueRef.current) {
        dragClueRef.current.hide();
      }
      setIsDragging(false);

      if (event.itemHierarchicalIndex === "0") return;

      const analyzer = new TreeViewDragAnalyzer(event).init();
      if (!analyzer.isDropAllowed) return;

      const op = analyzer.getDropOperation();
      if (op !== "before" && op !== "after") return;

      const updated = moveTreeViewItem(
        event.itemHierarchicalIndex,
        treeData,
        op,
        analyzer.destinationMeta.itemHierarchicalIndex
      );

      // Save the new order to tile-specific config
      const newOrder = updated[0].items.map((i) => i.id);
      const currentConfig = tileTreeViewConfigs.value[tileId];

      if (currentConfig) {
        const updatedTreeViews = currentConfig.treeViews.map((tv) =>
          tv.id === treeViewConfig.id ? { ...tv, itemOrder: newOrder } : tv
        );

        tileTreeViewConfigs.value = {
          ...tileTreeViewConfigs.value,
          [tileId]: {
            ...currentConfig,
            treeViews: updatedTreeViews,
          },
        };
      }
    },
    [treeData, tileId, treeViewConfig.id]
  );

  const onExpandChange = useCallback(
    (event) => {
      if (event.item.text === treeViewConfig.name) {
        const newExpanded = !expanded;
        setExpanded(newExpanded);
        // Persist expanded state to config
        const currentConfig = tileTreeViewConfigs.value[tileId];
        if (currentConfig) {
          const updatedTreeViews = currentConfig.treeViews.map((tv) =>
            tv.id === treeViewConfig.id ? { ...tv, expanded: newExpanded } : tv
          );
          tileTreeViewConfigs.value = {
            ...tileTreeViewConfigs.value,
            [tileId]: {
              ...currentConfig,
              treeViews: updatedTreeViews,
            },
          };
        }
      }
    },
    [expanded, treeViewConfig.name, treeViewConfig.id, tileId]
  );

  const getCursorStyle = (isDraggable, isHovering, isDragging) => {
    if (!isDraggable || !isHovering) {
      return "default";
    }
    return isDragging ? "grabbing" : "grab";
  };

  // Item renderer
  const renderTreeItem = useCallback(
    ({ item }) => {
      // Root node (TreeView header) - has treeViewConfig property
      if (item.treeViewConfig) {
        return (
          <div className="treeview-root-node">
            <div className="treeview-root-node-content">
              <ColorChangingIcon
                icon={iconConfig.icon}
                initialPrimaryColor={iconConfig.primaryColor}
                initialSecondaryColor={iconConfig.secondaryColor}
                initialSize={16}
                hoverSize={16}
              />
              <span>{item.text}</span>
            </div>
            {/* Config gear icon - only show when lock is disabled */}
            {!isLocked && (
              <ColorChangingIcon
                icon={faGear}
                initialPrimaryColor="#6e6e6e"
                initialSecondaryColor="#919191"
                initialSize={16}
                hoverSize={18}
                onClick={(e) => {
                  e.stopPropagation();
                  onConfigClick(treeViewConfig.id);
                }}
              />
            )}
          </div>
        );
      }

      const isItemExpanded = expandedItems[item.id] || false;

      // Dataset item
      if (item.itemType === "dataset" && item.datasetData) {
        return (
          <div className="treeview-item-container">
            <div className="treeview-item-content">
              <ColorChangingIcon
                icon={faFolder}
                initialPrimaryColor="#FFD700"
                initialSecondaryColor="#FFA500"
                initialSize={16}
                hoverSize={16}
              />
              <span className="treeview-item-text">{item.text}</span>
            </div>
            {/* Only show angle icon and buttons when drag is disabled */}
            {!isDragEnabled && (
              <div className="treeview-item-actions">
                {isItemExpanded && (
                  <>
                    <EditDatasetButton dataItem={item.datasetData} />
                    <ViewDatasetButton dataItem={item.datasetData} />
                  </>
                )}
                <ColorChangingIcon
                  icon={isItemExpanded ? faAngleLeft : faAngleRight}
                  initialPrimaryColor="#6e6e6e"
                  initialSecondaryColor="#919191"
                  initialSize={16}
                  hoverSize={18}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleItemExpanded(item.id);
                  }}
                />
              </div>
            )}
          </div>
        );
      }

      // Template item (default)
      if (item.templateData) {
        return (
          <div className="treeview-item-container">
            <div className="treeview-item-content">
              <ColorChangingIcon
                icon={faWindowRestore}
                initialPrimaryColor="#4a90d9"
                initialSecondaryColor="#2e5a87"
                initialSize={16}
                hoverSize={16}
              />
              <span className="treeview-item-text">{item.text}</span>
            </div>
            {/* Only show angle icon and buttons when drag is disabled */}
            {!isDragEnabled && (
              <div className="treeview-item-actions">
                {isItemExpanded && (
                  <>
                    <ViewEntryTemplateButton dataItem={item.templateData} />
                    <CreateEntryTemplateButton dataItem={item.templateData} />
                    <EditTemplateButton dataItem={item.templateData} />
                    <ViewTemplateButton dataItem={item.templateData} />
                  </>
                )}
                <ColorChangingIcon
                  icon={isItemExpanded ? faAngleLeft : faAngleRight}
                  initialPrimaryColor="#6e6e6e"
                  initialSecondaryColor="#919191"
                  initialSize={16}
                  hoverSize={18}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleItemExpanded(item.id);
                  }}
                />
              </div>
            )}
          </div>
        );
      }

      return <span>{item.text}</span>;
    },
    [iconConfig, onConfigClick, treeViewConfig, isDragEnabled, expandedItems, toggleItemExpanded]
  );

  // Draggable is only enabled when lock is active AND drag mode is enabled
  const isDraggable = isLocked && isDragEnabled;
  const cursor = getCursorStyle(isDraggable, isHovering, isDragging);

  // Sync treeData with local expanded state for root node collapse/expand
  const syncedTreeData = treeData.map((item) => ({
    ...item,
    expanded,
  }));

  return (
    <div
      ref={containerRef}
      className="treeview-container"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Lock/Unlock button */}
      {isLocked && (
        <div
          className={`treeview-lock-button ${isDragEnabled ? "enabled" : ""}`}
          onClick={toggleDragMode}
          title={isDragEnabled ? "Disable reordering" : "Enable reordering"}
        >
          <ColorChangingIcon
            icon={isDragEnabled ? faLockOpen : faLock}
            initialPrimaryColor={isDragEnabled ? "#4a90d9" : "#6e6e6e"}
            initialSecondaryColor={isDragEnabled ? "#2e5a87" : "#919191"}
            initialSize={15}
            hoverSize={15}
          />
        </div>
      )}
      <TreeView
        style={{ cursor }}
        data={syncedTreeData}
        draggable={isDraggable}
        expandIcons
        item={renderTreeItem}
        onExpandChange={onExpandChange}
        onItemDragStart={() => setIsDragging(true)}
        onItemDragOver={onItemDragOver}
        onItemDragEnd={onItemDragEnd}
      />
      <TreeViewDragClue ref={dragClueRef} />
    </div>
  );
};
